import * as React from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { Modal, ModalContent } from '@restworld/ui-ds-base';
import type { JobApplication } from '@restworld/utility-types';
import { TabComponentType, useAnalytics } from '@restworld/utils-common';
import { readStoredToken, useAuth } from '@restworld/feature-authentication';

import {
  activeTabAtom,
  createdJobApplicationAtom,
  inAppAssessmentConfirmationAtom,
  isCVUploadProcessCompletedAtom,
  resetWorkerEntryStates,
  showConfirmModalAtom,
  spontaneousApplyDataAtom,
  uploadCVLoadingAtom,
  uploadFilesAtom,
  workerEntryPointErrorsAtom,
  workerIdAtom,
} from './worker-entry-point.atoms';
import {
  EntryProcessButtons,
  JobApplyConfirmation,
  EntryPointChecklistProvider,
  useEntryPointCheckListContext,
} from './worker-entry-point.components';
import { LoginPrivacyRedirectSubmitButton } from './worker-entry-point.components/entry-component-new/privacy-terms';
import { jobPositionApiDataAtom, jobPositionApplyStatusAtom } from '../job-position/job-position.atoms';
import { workerManagedUsingKlaaryoModalFlagAtom, workerManagedFooterMapKeyAtom } from '../worker-managed-using-klaaryo';
import { EntryPointModalBody } from './entry-point-modal-body';
import { reportAssessmentChannelMissing } from './worker-entry-point.components/in-app-assessment-observability';
import { WorkerCVLoadingFooterCTA } from './worker-cvloading-ui';
import {
  SpontaneousCoverLetterEntryContextProvider,
  useSpontaneousCoverLetterEntryContext,
} from '../cover-letter-application/spontaneous-cover-letter-entry/spontaneous-apply-context';
import { useApplyJob, useJobPositionId } from '../../hooks';
import { saveResearchModalV2Atom } from '../job-explorer/job-explorer-restaurant-explorer.atoms';
import { isWorkerNavOverlayOpenAtom } from '../../layouts/wrapper-layout.atoms';
import { APPLY_INTENT_PARAM } from './apply-intent';
import { claimApplyIntent, resumeTargetFor } from './apply-intent-guard';

interface IWorkerEntryPointModalProps {
  hideBackBtn?: boolean;
  /**
   * Renders this instance independently of the navbar and job-position ones,
   * driven by `standaloneEntryPointAtom`. Needed by screens that have no
   * `WorkerEntryPointModal` of their own: `/login` renders no
   * `DefaultNavigationBar` at all, and everywhere else the navbar instance is
   * mounted only while the side menu is open (see `additionalNavMenuContentEl`
   * in `default-navigation-bar.tsx`), so flipping `entryPointModalVisibleAtom`
   * from the login form opened nothing.
   */
  standalone?: boolean;
}
export const entryPointModalVisibleAtom = atom<boolean>(false);
/** True while the standalone instance owns the entry-point modal. */
export const standaloneEntryPointAtom = atom<boolean>(false);
/**
 * Opens the entry point straight on the "upload your CV" step, on the standalone
 * instance. Use it from any screen that does not mount a `WorkerEntryPointModal`
 * itself; it is the same destination as the navbar "Carica CV" entry.
 */
export const openStandaloneUploadCvEntryPoint = atom(null, (_get, set) => {
  set(activeTabAtom, 'upload-cv');
  set(standaloneEntryPointAtom, true);
  set(entryPointModalVisibleAtom, true);
});

const WorkerEntryPointModal: React.FunctionComponent<IWorkerEntryPointModalProps> = ({
  hideBackBtn,
  standalone = false,
}) => {
  const [spontaneousApplyData] = useAtom(spontaneousApplyDataAtom);
  const [showEntryPoint, toggleEntryPoint] = useAtom(entryPointModalVisibleAtom);
  const [isCVUploadProcessCompleted, setCVUploadedProcessComplete] = useAtom(isCVUploadProcessCompletedAtom);
  const [, uploadCVLoadingToggler] = useAtom(uploadCVLoadingAtom);
  const { initializeAuthentication } = useAuth();
  const activeTab = useAtomValue(activeTabAtom);
  const uploadFiles = useAtomValue(uploadFilesAtom);
  const [, confirmModalToggler] = useAtom(showConfirmModalAtom);
  const isNavOverlayOpen = useAtomValue(isWorkerNavOverlayOpenAtom);
  const [isStandaloneOpen, setStandaloneOpen] = useAtom(standaloneEntryPointAtom);
  const setActiveTab = useSetAtom(activeTabAtom);
  const closeEntryPoint = React.useCallback(() => toggleEntryPoint(false), [toggleEntryPoint]);
  /** Modal B is on screen: no X, no backdrop click, no Escape. See the comment on `<Modal>`. */
  const isInterviewConfirmation = useAtomValue(inAppAssessmentConfirmationAtom) === 'returning';
  const setCreatedJobApplication = useSetAtom(createdJobApplicationAtom);

  const { trackEvent } = useAnalytics();

  const handleCVUploadProcess = React.useCallback(() => {
    if (isCVUploadProcessCompleted) uploadCVLoadingToggler(false);
  }, [isCVUploadProcessCompleted, uploadCVLoadingToggler]);

  const handleSpontaneousApply = React.useCallback(() => {
    // Same storage hazard as the two provider-level reads, and read once rather than twice: an
    // unavailable store used to throw here instead of being treated as "no token". This runs from a
    // click, so it escapes the ErrorBoundary rather than crashing the page, which is why it only
    // broke the spontaneous-apply flow.
    const storedToken = readStoredToken();
    if (spontaneousApplyData?.didUserSpontaneousApply && storedToken) initializeAuthentication(storedToken);
  }, [initializeAuthentication, spontaneousApplyData?.didUserSpontaneousApply]);

  const handleActiveTabEvents = React.useCallback(() => {
    if (isCVUploadProcessCompleted) trackEvent('check_data_cv', { close_button: false });
    if (activeTab === 'upload-cv')
      trackEvent('upload_cv', {
        close_button: true,
        upload_media: (uploadFiles?.length && uploadFiles.length > 0) ?? false,
      });
    if (activeTab === 'application-wizard')
      trackEvent('initiate_questionnaire', { close_button: false, terms_and_conditions: false });
  }, [activeTab, isCVUploadProcessCompleted, trackEvent, uploadFiles?.length]);

  const handleOtherOperation = React.useCallback(() => {
    confirmModalToggler(false);
    setCVUploadedProcessComplete(false);
    // Otherwise the next application opened in this session would be drawn from the previous
    // one's channel, which is the kind of fault that only shows on the second run.
    setCreatedJobApplication(undefined);

    handleCVUploadProcess();
    handleSpontaneousApply();
    handleActiveTabEvents();
  }, [
    confirmModalToggler,
    handleActiveTabEvents,
    handleCVUploadProcess,
    handleSpontaneousApply,
    setCVUploadedProcessComplete,
    setCreatedJobApplication,
  ]);

  const onHandleClose = React.useCallback(() => {
    closeEntryPoint();
    setTimeout(() => {
      handleOtherOperation();
    }, 250);
  }, [closeEntryPoint, handleOtherOperation]);

  // `standalone` implies no back button: the CTA that opens it lands directly on
  // the upload step, so there is no choice screen to go back to. It also keeps
  // `isRelatedToJp` false, which is what stops the CV upload from auto-applying
  // to whatever job position happens to be loaded.
  const hideBack = hideBackBtn || standalone;
  const isRelatedToJp = !hideBack;
  useEntryPointModal({ isRelatedToJp, isNavOverlayOpen, standalone });

  // The standalone instance is mounted app-wide, so it has to release the shared
  // state once its modal closes, either through `onHandleClose` or through the
  // post-CV button, which flips `entryPointModalVisibleAtom` directly. The delay
  // outlives BaseModal's longest leave transition (300ms) so the close animation
  // is not cut short by the unmount, and resetting the tab keeps the next entry
  // point (a job apply) on its choice screen.
  React.useEffect(() => {
    if (!standalone || !isStandaloneOpen || showEntryPoint) return;
    const timer = setTimeout(() => {
      setStandaloneOpen(false);
      setActiveTab('');
    }, 350);
    return () => clearTimeout(timer);
  }, [isStandaloneOpen, setActiveTab, setStandaloneOpen, showEntryPoint, standalone]);

  if (standalone) {
    if (!isStandaloneOpen) return null;
  } else {
    // While the standalone instance owns the modal, the job-position one would
    // stack a second BaseModal on top of it.
    if (isStandaloneOpen) return null;
    if ((isRelatedToJp && isNavOverlayOpen) || (!isRelatedToJp && !isNavOverlayOpen)) return null;
  }

  return (
    <Modal
      open={showEntryPoint}
      // The returning worker's interview confirmation has no close X (Andrea's design,
      // decision 106), so it must have no backdrop or Escape dismissal either: an "inescapable"
      // modal that Escape still closes is inescapable only for the worker who does not know
      // about Escape. Closing those two as well is a reading of the design rather than a line
      // of it, and is flagged as a product point for Edoardo.
      disableOverlayClose={isInterviewConfirmation}
      onOpenChange={(v) => {
        if (v || isInterviewConfirmation) return;
        onHandleClose();
      }}
    >
      {/* Provider lifted here so the header back button + the footer CTAs (both rendered by
          ModalContent) can share the same checklist context as the scrollable body. */}
      <EntryPointChecklistProvider>
        {/* Lifted above ModalContent so the fixed header (back/close) can read the cover-letter
            section state. The nested provider inside CoverLetterEntry is idempotent and defers to this one. */}
        <SpontaneousCoverLetterEntryContextProvider>
          <WorkerEntryPointModalContent hideBackBtn={hideBack} />
        </SpontaneousCoverLetterEntryContextProvider>
      </EntryPointChecklistProvider>
    </Modal>
  );
};

// The header (back + X), the scrollable body, and the fixed footer (CTAs). The back button and
// footer live in ModalContent (not the body) so only the middle content scrolls.
const WorkerEntryPointModalContent = ({ hideBackBtn }: { hideBackBtn?: boolean }) => {
  const activeTab = useAtomValue(activeTabAtom);
  const setActiveTab = useSetAtom(activeTabAtom);
  const setUploadFiles = useSetAtom(uploadFilesAtom);
  const [spontaneousApplyData, setSpontaneousApplyData] = useAtom(spontaneousApplyDataAtom);
  const showConfirmModal = useAtomValue(showConfirmModalAtom);
  const errors = useAtomValue(workerEntryPointErrorsAtom);
  const uploadCVLoading = useAtomValue(uploadCVLoadingAtom);
  const isCVUploadProcessCompleted = useAtomValue(isCVUploadProcessCompletedAtom);
  const jobPositionApplyStatus = useAtomValue(jobPositionApplyStatusAtom);
  const inAppAssessmentConfirmation = useAtomValue(inAppAssessmentConfirmationAtom);
  const { isAuthenticated } = useAuth();
  const { setIsCVNotUploaded, setBtnTriggeredWithoutCheck } = useEntryPointCheckListContext();
  const { coverLetterModalSection, onChangeCoverLetterModalSection, isExitConfirmation } =
    useSpontaneousCoverLetterEntryContext();

  const isSubScreen = activeTab === 'application-wizard' || activeTab === 'upload-cv';
  // Cover-letter "write" screen: enable the shared header's Back so it returns to the confirmation step.
  const isCoverLetterInputArea =
    !!spontaneousApplyData?.isSpontaneousEntry &&
    !!spontaneousApplyData?.showCoverLetter &&
    coverLetterModalSection === 'cover-letter-input-area' &&
    !isExitConfirmation;
  // States where the body isn't the upload/questionnaire screen (CV parsing, cover letter,
  // errors, apply-confirmation) — no back button and no custom footer on those.
  const isBlockingState =
    showConfirmModal ||
    (!!errors?.statusCode && errors.statusCode >= 400 && errors.statusCode <= 409) ||
    (!!spontaneousApplyData?.isSpontaneousEntry && !!spontaneousApplyData?.showCoverLetter) ||
    uploadCVLoading ||
    isCVUploadProcessCompleted ||
    (isAuthenticated && !!jobPositionApplyStatus);
  const showBackButton = (isSubScreen && !isBlockingState && !hideBackBtn) || isCoverLetterInputArea;

  // Back button (now in the header, next to the X): return to the initial screen and clear the
  // sticky `entryType` so the initial screen goes back to md:max-w-lg (a stale
  // 'application-wizard' entryType had widened it to md:max-w-fit).
  const onBack = React.useCallback(() => {
    // In the cover-letter "write" screen, Back returns to the confirmation step (not the initial screen).
    if (isCoverLetterInputArea) {
      onChangeCoverLetterModalSection('cover-letter-confirmation');
      return;
    }
    setActiveTab('');
    setIsCVNotUploaded(false);
    setBtnTriggeredWithoutCheck(false);
    setUploadFiles(null);
    setSpontaneousApplyData((p) => ({ ...p, entryType: undefined }));
  }, [
    isCoverLetterInputArea,
    onChangeCoverLetterModalSection,
    setActiveTab,
    setBtnTriggeredWithoutCheck,
    setIsCVNotUploaded,
    setSpontaneousApplyData,
    setUploadFiles,
  ]);

  // Footer (fixed, non-scrolling). On the sub-screens it holds the terms + primary CTA + toggle;
  // on the initial screen the Upload/Questionnaire buttons.
  const footerEl = React.useMemo(() => {
    // CV parsing / CV-completed ("Candidatura inviata"): pin the primary CTA in the footer so it
    // stays fixed at the bottom while only the loading/summary content scrolls (the CTA used to
    // live inside the scrollable body). This is a blocking state, so handle it before the guard.
    // Exclude the spontaneous cover-letter / success screen: its body is its own confirmation
    // (CoverLetterEntry / applied-confirmation) that already renders "Controlla il tuo profilo",
    // so the footer CTA there is a duplicate.
    // Modal A carries its own two buttons in its body, so the CV screen's footer CTA would be
    // a third one under them. Every other CV ending keeps that footer exactly as today.
    if (inAppAssessmentConfirmation === 'new') return undefined;
    if (
      (uploadCVLoading || isCVUploadProcessCompleted) &&
      !(spontaneousApplyData?.isSpontaneousEntry && spontaneousApplyData?.showCoverLetter)
    )
      return <WorkerCVLoadingFooterCTA />;
    if (isBlockingState) return undefined;
    // key per screen so the local "privacy checked" state resets when switching upload<->wizard
    if (isSubScreen) return <LoginPrivacyRedirectSubmitButton key={activeTab} type={activeTab as TabComponentType} />;
    return <EntryProcessButtons />;
  }, [
    activeTab,
    isBlockingState,
    isSubScreen,
    uploadCVLoading,
    isCVUploadProcessCompleted,
    spontaneousApplyData?.isSpontaneousEntry,
    spontaneousApplyData?.showCoverLetter,
    inAppAssessmentConfirmation,
  ]);

  return (
    <ModalContent
      showCloseIcon={inAppAssessmentConfirmation !== 'returning'}
      className={`min-h-[20rem] max-w-full w-full md:max-w-lg md:min-w-104 ${
        spontaneousApplyData?.showCoverLetter ? 'md:max-w-2xl' : ''
      } ${activeTab === 'application-wizard' ? 'p-6 md:max-w-fit' : ''}`}
      noPadding={activeTab === 'application-wizard'}
      showBackButton={showBackButton}
      onBack={onBack}
      footer={footerEl}
    >
      <div className="mx-auto">
        <ApplicationModalBody hideBackBtn={hideBackBtn} />
      </div>
    </ModalContent>
  );
};

export default WorkerEntryPointModal;
const ApplicationModalBody = ({ hideBackBtn }: { hideBackBtn?: boolean }) => {
  const showConfirmModal = useAtomValue(showConfirmModalAtom);
  return showConfirmModal ? <JobApplyConfirmation /> : <EntryPointModalBody hideBackBtn={hideBackBtn} />;
};

export const useEntryPointModal = ({
  isRelatedToJp,
  isNavOverlayOpen,
  standalone,
}: {
  isRelatedToJp?: boolean;
  isNavOverlayOpen?: boolean;
  standalone?: boolean;
} = {}) => {
  const [, toggleEntryPoint] = useAtom(entryPointModalVisibleAtom);

  const closeEntryPoint = React.useCallback(() => toggleEntryPoint(false), [toggleEntryPoint]);
  const openEntryPoint = React.useCallback(() => toggleEntryPoint(true), [toggleEntryPoint]);

  const [, setJobPositionApplyStatus] = useAtom(jobPositionApplyStatusAtom);

  const router = useRouter();
  const { query: queryParams } = router;
  const openWorkerEntryPoint = queryParams['apply'] as string;

  const workerId = useAtomValue(workerIdAtom);
  const [isCVUploadProcessCompleted, setCVUploadedProcessComplete] = useAtom(isCVUploadProcessCompletedAtom);
  const confirmModalToggler = useSetAtom(showConfirmModalAtom);
  const [spontaneousApplyData, setSpontaneousApplyData] = useAtom(spontaneousApplyDataAtom);
  const setErrors = useSetAtom(uploadFilesAtom);
  const stateResetFn = useSetAtom(resetWorkerEntryStates);
  const setCreatedJobApplication = useSetAtom(createdJobApplicationAtom);

  const jobPositionId = useJobPositionId();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const setKlaaryoModalFlag = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  const setKlaaryoFooterKey = useSetAtom(workerManagedFooterMapKeyAtom);
  const isAtsWithApplyUrl = !!jobPositionApiData?.ats_applyurl;

  const { applyJobPosition } = useApplyJob({
    callUserAuth: !spontaneousApplyData?.isSpontaneousEntry,
    referralCode: router?.query?.['scout'],
    onUnAuthenticated: () => {
      stateResetFn();
      setCVUploadedProcessComplete(true);
      if (spontaneousApplyData?.isSpontaneousEntry) setSpontaneousApplyData((p) => ({ ...p, showCoverLetter: true }));
      // ATS with apply URL (e.g. TeamTailor): show redirect modal immediately
      if (isAtsWithApplyUrl) {
        setKlaaryoModalFlag(true);
        setKlaaryoFooterKey('direct_applied');
        closeEntryPoint();
      }
    },
    onAuthenticated: () => {
      if (openEntryPoint) openEntryPoint();
      if (setJobPositionApplyStatus) setJobPositionApplyStatus(true);
      if (user?.type === 'lead') setCVUploadedProcessComplete(true);
      // ATS with apply URL (e.g. TeamTailor): show redirect modal immediately
      if (isAtsWithApplyUrl) {
        setKlaaryoModalFlag(true);
        setKlaaryoFooterKey('direct_applied');
        closeEntryPoint();
      }
    },
    errorCallBack: (e: unknown) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (e) setErrors({ statusCode: e?.['code'], code: e?.['code'] } as IErrorProps);
    },
  });

  // The `?apply=true` intent is a one-shot: it survives in history, so a refresh or a back
  // navigation would otherwise re-open the confirmation and invite a second application. The
  // guard lives in `apply-intent-guard.ts`, in module scope, because a ref here is reset by a
  // remount and the same intent was being answered about four times per person.

  React.useEffect(() => {
    // The `?apply=` deep link belongs to the job-position instance. The
    // standalone one is mounted on every page, so it would answer it too and
    // open the modal on screens that never asked for it.
    if (standalone) return;
    // Decision 114, and deliberate rather than an oversight: a worker who clicked "Candidati"
    // while logged out, signed in, and came back here lands on `JobApplyConfirmation` — the
    // RETURNING worker's screen — even though they were not authenticated at the first click.
    // They already had an account and were only using it, so that is the right screen; the
    // "new" confirmation belongs to the worker whose account did not exist before the flow.
    if (openWorkerEntryPoint === 'true' && claimApplyIntent(router.asPath)) {
      // A venue page has no offer to confirm, and the confirmation screen there could not
      // work. `spontaneousApplyData` is already filled on mount by `entry-point-button.tsx`,
      // so opening the entry point alone lands on the spontaneous path instead. The reasoning
      // and the production numbers are in `apply-intent-guard.ts`.
      const target = resumeTargetFor({ pathname: router.pathname, jobPositionId });
      if (target.openConfirmation) confirmModalToggler(true);
      if (openEntryPoint) openEntryPoint();
      trackEvent('apply_intent_resumed', {
        // The id is only this page's on a job-position route; on a venue it is whatever offer
        // the visitor looked at earlier in the tab, so it is not reported there.
        job_position_id: target.surface === 'job_position' ? jobPositionId || null : null,
        // A null offer is correct on a venue and wrong anywhere else, and until now the two
        // were written the same way.
        surface: target.surface,
      });
      const { [APPLY_INTENT_PARAM]: _consumed, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
    if (openWorkerEntryPoint === 'false' && closeEntryPoint) closeEntryPoint();
  }, [
    openWorkerEntryPoint,
    openEntryPoint,
    closeEntryPoint,
    confirmModalToggler,
    standalone,
    router,
    trackEvent,
    jobPositionId,
  ]);

  React.useEffect(() => {
    if (workerId && isCVUploadProcessCompleted && isRelatedToJp && !isNavOverlayOpen) {
      const isSpontaneous = !!spontaneousApplyData?.isSpontaneousEntry;
      applyJobPosition({
        workerIdArg: workerId,
        ...(isSpontaneous ? { restaurantId: spontaneousApplyData.restaurantID } : { jobPositionId }),
        source: 'cv',
      }).then((created) => {
        // What the CV screen needs to know to draw modal A instead of its usual ending. A
        // spontaneous application has no interview to send anyone to, so it is left out.
        if (isSpontaneous) return;
        const application = (created as { job_application?: Partial<JobApplication> })?.job_application;
        const channel = application?.assessment_channel;
        // Reported, never acted on: the CV screen below is the right outcome either way.
        if (channel !== 'in_app' && channel !== 'whatsapp')
          reportAssessmentChannelMissing({ channel, surface: 'cv_upload_flow', jobPositionId });
        setCreatedJobApplication(application);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, isCVUploadProcessCompleted, spontaneousApplyData?.isSpontaneousEntry, setSpontaneousApplyData]);
};

export const useSaveResearchModalForJobPosition = () => {
  const [, setSaveResearchModal] = useAtom(saveResearchModalV2Atom);
  // const setAreaSelection = useSetAtom(areaSelectionAtom);
  const router = useRouter();

  const openSaveResearchModal = React.useCallback(() => {
    if (router.pathname.includes('/posizione')) {
      setTimeout(() => {
        setSaveResearchModal(true);
        // setAreaSelection('regional');
      }, 200);
    }
  }, [router.pathname, setSaveResearchModal]);

  return { openSaveResearchModal };
};
